import { useEffect } from "react";
import {
  Mail,
  MessageSquare,
  Globe,
  X as XIcon,
  ShieldCheck,
  Sparkles,
} from "lucide-react";

/**
 * CampaignPreview — full-screen modal that renders each enabled channel of
 * a campaign exactly the way the consumer will see it.
 *
 * Email     → desktop client wrapper (subject, sender, rendered blocks)
 * SMS       → phone-frame bubble with character count + opt-out reminder
 * Landing   → standalone landing page (hero + rendered blocks)
 *
 * Read-only. Closes on Esc or backdrop click.
 */
export default function CampaignPreview({ campaign, onClose }) {
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  if (!campaign) return null;

  const channels = (campaign.channels || []).filter((c) => c.enabled !== false);
  const hasLanding = campaign.landing_page || channels.some((c) => c.channel === "landing_page");

  return (
    <div
      data-testid="campaign-preview-overlay"
      className="fixed inset-0 z-[60] bg-slate-900/60 backdrop-blur-sm flex items-stretch justify-center overflow-y-auto"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="w-full max-w-5xl my-6 mx-4 rounded-2xl bg-slate-50 border border-slate-200 shadow-2xl overflow-hidden flex flex-col">
        {/* Header */}
        <div className="bg-white border-b border-slate-200 px-5 py-3 flex items-center justify-between sticky top-0 z-10">
          <div className="flex items-center gap-2 min-w-0">
            <div className="inline-flex w-8 h-8 rounded-full bg-violet-50 items-center justify-center">
              <Sparkles className="w-4 h-4 text-violet-600" />
            </div>
            <div className="min-w-0">
              <div className="text-[11px] uppercase tracking-[0.18em] font-bold text-slate-500">
                Campaign preview
              </div>
              <div className="font-display font-bold text-slate-900 truncate">
                {campaign.name}
              </div>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            data-testid="campaign-preview-close"
            className="p-1.5 rounded-full hover:bg-slate-100 text-slate-500"
            aria-label="Close preview"
          >
            <XIcon className="w-4 h-4" />
          </button>
        </div>

        {/* Compliance reminder */}
        <div className="bg-amber-50/60 border-b border-amber-200 px-5 py-2 text-xs text-amber-900 flex items-center gap-2">
          <ShieldCheck className="w-3.5 h-3.5 text-amber-700 shrink-0" />
          <span>
            Read-only preview. Confirm copy is educational and engagement-focused —
            no medical claims, no guaranteed outcomes, no PHI.
          </span>
        </div>

        {/* Content */}
        <div className="flex-1 p-5 space-y-6">
          {channels.length === 0 && !hasLanding && (
            <div className="rounded-2xl border border-dashed border-slate-300 bg-white p-10 text-center text-sm text-slate-500">
              This campaign has no channels enabled yet. Add an email, SMS, or
              landing page in the builder to preview it here.
            </div>
          )}

          {channels.map((ch) => {
            if (ch.channel === "email") return <EmailPreview key="email" channel={ch} />;
            if (ch.channel === "sms") return <SMSPreview key="sms" channel={ch} />;
            if (ch.channel === "landing_page")
              return (
                <LandingPreview
                  key="landing"
                  channel={ch}
                  landingPage={campaign.landing_page}
                />
              );
            return null;
          })}
          {/* If there's a landing_page payload but no landing_page channel,
              show it anyway so creators don't lose visibility. */}
          {campaign.landing_page && !channels.some((c) => c.channel === "landing_page") && (
            <LandingPreview channel={null} landingPage={campaign.landing_page} />
          )}
        </div>
      </div>
    </div>
  );
}

// =========================================================
// Email preview — desktop email client wrapper
// =========================================================
function EmailPreview({ channel }) {
  return (
    <section data-testid="campaign-preview-email">
      <ChannelHeader icon={Mail} label="Email" />
      <div className="rounded-2xl border border-slate-200 bg-white shadow-sm overflow-hidden">
        <div className="px-5 py-3 border-b border-slate-100 bg-slate-50 text-sm">
          <div className="text-slate-500">
            <span className="font-semibold text-slate-700">From:</span>{" "}
            {channel.sender_name || "Your Clinic"}{" "}
            <span className="text-slate-400">&lt;noreply@your-clinic.com&gt;</span>
          </div>
          <div className="text-slate-900 font-display font-bold mt-1">
            {channel.subject || (
              <span className="italic text-slate-400">(no subject)</span>
            )}
          </div>
        </div>
        <div className="px-6 py-6 max-w-2xl mx-auto">
          {(channel.blocks || []).length === 0 ? (
            <div className="text-sm text-slate-400 italic">
              No content blocks added yet — open the builder to add a headline,
              paragraph, or CTA.
            </div>
          ) : (
            <BlockRenderer blocks={channel.blocks} />
          )}
          <FooterDisclaimer kind="email" />
        </div>
      </div>
    </section>
  );
}

// =========================================================
// SMS preview — phone-frame bubble
// =========================================================
function SMSPreview({ channel }) {
  const body = channel.body_text || "";
  const length = body.length;
  const overLimit = length > 160;
  const hasOptOut = /STOP/i.test(body);
  return (
    <section data-testid="campaign-preview-sms">
      <ChannelHeader icon={MessageSquare} label="SMS" />
      <div className="flex flex-col sm:flex-row gap-5 items-start">
        {/* Phone frame */}
        <div className="mx-auto sm:mx-0 w-[280px] shrink-0 rounded-[2rem] border-[10px] border-slate-900 bg-slate-100 p-2 shadow-xl">
          <div className="rounded-[1.4rem] bg-white p-3 min-h-[400px]">
            <div className="text-[10px] text-center text-slate-400 mb-3 font-semibold uppercase tracking-wider">
              {channel.sender_name || "Your Clinic"}
            </div>
            <div className="bg-slate-100 text-slate-900 text-sm rounded-2xl rounded-bl-md px-3 py-2 max-w-[230px] whitespace-pre-wrap break-words">
              {body || (
                <span className="italic text-slate-400">
                  (SMS body is empty)
                </span>
              )}
            </div>
            <div className="text-[10px] text-slate-400 mt-1 ml-1">now</div>
          </div>
        </div>
        {/* Meta */}
        <div className="flex-1 space-y-3 text-sm">
          <Stat
            label="Character count"
            value={`${length}/160`}
            tone={overLimit ? "warn" : "ok"}
          />
          <Stat
            label="Opt-out language"
            value={hasOptOut ? "Detected (Reply STOP)" : "MISSING"}
            tone={hasOptOut ? "ok" : "warn"}
          />
          <Stat
            label="Sender name"
            value={channel.sender_name || "Your Clinic"}
            tone="info"
          />
          {!hasOptOut && (
            <div className="rounded-xl border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900">
              Add &quot;Reply STOP to opt out.&quot; to comply with SMS marketing rules.
            </div>
          )}
        </div>
      </div>
    </section>
  );
}

// =========================================================
// Landing page preview — full-bleed marketing page
// =========================================================
function LandingPreview({ channel, landingPage }) {
  // Two sources: builder's landing_page hero metadata (headline/subhead/cta_label)
  // + the channel.blocks from the landing-page channel.
  const blocks = (channel?.blocks?.length ? channel.blocks : landingPage?.blocks) || [];
  const hero = landingPage || {};
  return (
    <section data-testid="campaign-preview-landing">
      <ChannelHeader icon={Globe} label="Landing page" />
      <div className="rounded-2xl border border-slate-200 bg-white overflow-hidden shadow-sm">
        {/* Browser chrome */}
        <div className="bg-slate-100 border-b border-slate-200 px-3 py-2 flex items-center gap-2">
          <span className="w-2.5 h-2.5 rounded-full bg-rose-300" />
          <span className="w-2.5 h-2.5 rounded-full bg-amber-300" />
          <span className="w-2.5 h-2.5 rounded-full bg-emerald-300" />
          <div className="flex-1 ml-3 bg-white rounded-md px-2 py-1 text-[11px] text-slate-500 truncate">
            https://your-clinic.example.com/programs
          </div>
        </div>
        {/* Hero */}
        <div className="px-6 sm:px-12 py-12 bg-gradient-to-br from-slate-900 to-slate-800 text-white text-center">
          <h1 className="font-display text-2xl sm:text-4xl font-extrabold tracking-tight">
            {hero.headline || (
              <span className="italic text-slate-400">(landing-page headline)</span>
            )}
          </h1>
          {hero.subhead && (
            <p className="mt-3 text-sm sm:text-base text-slate-200 max-w-xl mx-auto">
              {hero.subhead}
            </p>
          )}
          {hero.cta_label && (
            <div className="mt-6">
              <span className="inline-flex items-center gap-1.5 rounded-full bg-white text-slate-900 px-5 py-2.5 text-sm font-bold">
                {hero.cta_label}
              </span>
            </div>
          )}
        </div>
        {/* Body blocks */}
        <div className="px-6 sm:px-12 py-10 max-w-3xl mx-auto">
          {blocks.length === 0 ? (
            <div className="text-sm text-slate-400 italic">
              No landing-page blocks added yet.
            </div>
          ) : (
            <BlockRenderer blocks={blocks} />
          )}
          <FooterDisclaimer kind="landing" />
        </div>
      </div>
    </section>
  );
}

// =========================================================
// Block renderer — shared between email & landing
// =========================================================
function BlockRenderer({ blocks }) {
  return (
    <div className="space-y-5">
      {blocks.map((b) => {
        const c = b.content || {};
        if (b.type === "headline") {
          return (
            <h2
              key={b.id}
              className="font-display text-2xl font-bold text-slate-900 leading-tight"
            >
              {c.text || <span className="italic text-slate-400">(headline)</span>}
            </h2>
          );
        }
        if (b.type === "paragraph" || b.type === "education_section") {
          return (
            <p
              key={b.id}
              className="text-sm text-slate-700 leading-relaxed whitespace-pre-wrap"
            >
              {c.text || <span className="italic text-slate-400">(paragraph)</span>}
            </p>
          );
        }
        if (b.type === "cta_button") {
          return (
            <div key={b.id} className="pt-2">
              <span className="inline-flex items-center gap-1.5 rounded-full bg-slate-900 text-white px-5 py-2.5 text-sm font-bold">
                {c.label || "Learn more"}
              </span>
            </div>
          );
        }
        if (b.type === "scheduling_link") {
          return (
            <div
              key={b.id}
              className="rounded-xl border border-slate-200 bg-slate-50 p-3 text-sm"
            >
              <div className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-1">
                Schedule
              </div>
              <div className="text-slate-700">
                {c.text || c.label || "Book a consultation →"}
              </div>
            </div>
          );
        }
        if (b.type === "testimonial") {
          return (
            <blockquote
              key={b.id}
              className="border-l-4 border-blue-400 pl-3 py-1 text-slate-700 italic text-sm"
            >
              {c.text || "(testimonial)"}
            </blockquote>
          );
        }
        if (b.type === "faq") {
          const items = Array.isArray(c.items) && c.items.length ? c.items : [];
          return (
            <div key={b.id} className="space-y-2">
              <div className="text-xs font-bold text-slate-500 uppercase tracking-wider">
                Frequently asked
              </div>
              {items.length === 0 ? (
                <div className="text-sm text-slate-400 italic">(no FAQs added)</div>
              ) : (
                items.map((it, i) => (
                  <details
                    key={`${it.q || ""}-${i}`}
                    className="rounded-xl border border-slate-200 bg-white p-3"
                  >
                    <summary className="font-semibold text-sm text-slate-900 cursor-pointer">
                      {it.q || `Question ${i + 1}`}
                    </summary>
                    <div className="mt-2 text-sm text-slate-700">
                      {it.a || ""}
                    </div>
                  </details>
                ))
              )}
            </div>
          );
        }
        if (b.type === "image") {
          return (
            <div
              key={b.id}
              className="rounded-xl bg-slate-100 border border-slate-200 h-40 flex items-center justify-center text-xs text-slate-500 italic"
            >
              {c.alt || "(image placeholder)"}
            </div>
          );
        }
        if (b.type === "embed_form" || b.type === "appointment_prompt") {
          return (
            <div
              key={b.id}
              className="rounded-xl border border-dashed border-slate-300 bg-white p-4 text-sm text-slate-500 text-center"
            >
              {b.type === "embed_form" ? "[ Embedded form placeholder ]" : "[ Appointment prompt ]"}
            </div>
          );
        }
        return (
          <div
            key={b.id}
            className="rounded-xl border border-slate-200 bg-slate-50 p-3 text-xs text-slate-600"
          >
            <div className="font-bold uppercase tracking-wider">{b.type}</div>
            <pre className="mt-1 whitespace-pre-wrap">
              {JSON.stringify(c, null, 2)}
            </pre>
          </div>
        );
      })}
    </div>
  );
}

function ChannelHeader({ icon: Icon, label }) {
  return (
    <div className="flex items-center gap-2 mb-2">
      <span className="inline-flex items-center gap-1.5 bg-slate-900 text-white rounded-full px-3 py-1 text-[11px] font-bold uppercase tracking-wider">
        <Icon className="w-3 h-3" /> {label}
      </span>
    </div>
  );
}

function Stat({ label, value, tone = "info" }) {
  const tones = {
    ok: "bg-emerald-50 text-emerald-800 border-emerald-200",
    warn: "bg-amber-50 text-amber-800 border-amber-200",
    info: "bg-slate-50 text-slate-700 border-slate-200",
  };
  return (
    <div className={`rounded-xl border ${tones[tone]} px-3 py-2 flex items-center justify-between`}>
      <span className="text-xs font-semibold uppercase tracking-wider">{label}</span>
      <span className="font-bold text-sm">{value}</span>
    </div>
  );
}

function FooterDisclaimer({ kind }) {
  return (
    <div className="mt-8 pt-4 border-t border-slate-200 text-[11px] text-slate-500 leading-relaxed">
      <div>
        This message is educational and engagement-focused. It is not medical
        advice and does not substitute for a clinical consultation.
      </div>
      {kind === "email" && (
        <div className="mt-2">
          You're receiving this because you opted in to updates from your clinic.
          <br />
          <span className="underline">Unsubscribe</span> · <span className="underline">Update preferences</span>
        </div>
      )}
    </div>
  );
}
